import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PartnerMenu from "../layouts/partner_sidemenu";
import PartneHeader from "../layouts/partner_header";
import DashboardFooter from "../../institutions/layouts/dashboard_footer";
import Canvas from "../../institutions/layouts/dahboard_canvas";
import axios from "axios";

type Category = { id: number; name: string; slug: string };
type Clinic = { id: number; name: string };
type ServiceRequest = {
  id: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  creator?: { id?: number | null; name?: string | null };
  request_type?: string;
};
type Collaboration = {
  id: number;
  title: string;
  creating_partner: { id: number; name: string };
  patient?: any;
  ehr_access_granted: boolean;
  created_at: string;
  updated_at: string;
};

const ProfessionHome: React.FC = () => {
  const { professionSlug } = useParams();
  const baseUrl = (import.meta as any).env?.VITE_API_URL || (process as any).env?.REACT_APP_API_URL || "http://64.227.174.0:8004";

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [generalRequests, setGeneralRequests] = useState<ServiceRequest[]>([]);
  const [privateRequests, setPrivateRequests] = useState<ServiceRequest[]>([]);
  const [collaborations, setCollaborations] = useState<Collaboration[]>([]);

  const partnerName: any = "";
  const partnerId: any = "";
  const partnerCat: any = "";
  const partnerLogo: any = "";

  const clinicIds = useMemo(() => clinics.map(c => c.id), [clinics]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!professionSlug) return;
      setLoading(true);
      setError(null);
      try {
        // 1) Resolve category by slug
        const catsResp = await axios.get(`${baseUrl}/facilities/categories-with-slugs`);
        if (catsResp.data?.status !== "success") throw new Error(catsResp.data?.message || "Failed to load categories");
        const cats: any[] = catsResp.data.data || [];
        const found = cats.find((c: any) => c.slug === professionSlug);
        if (!found) throw new Error("Unknown profession");
        if (mounted) setCategory({ id: found.id, name: found.name, slug: found.slug });

        // 2) Fetch clinics in this category
        const fd = new FormData();
        fd.append('service_id', String(found.id));
        const clinicsResp = await axios.post(`${baseUrl}/facilities/clinics`, fd);
        if (clinicsResp.data?.status !== "success") throw new Error(clinicsResp.data?.message || "Failed to load clinics");
        const clinicList = (clinicsResp.data.data || []).map((cl: any) => ({ id: cl.clinic_id, name: cl.clinic_name })) as Clinic[];
        if (mounted) setClinics(clinicList);

        const clinicServices = await Promise.all(
          clinicList.map(async (cl) => {
            try {
              const svcResp = await axios.get(`${baseUrl}/facilities/services/clinic-services/${cl.id}/`);
              if (svcResp.data?.status === "success") {
                return (svcResp.data?.data?.services || []).map((s: any) => s.service_id as number);
              }
            } catch (_) {
              // ignore clinic service lookup failures for this clinic
            }
            return [] as number[];
          })
        );
        const serviceIds = Array.from(new Set<number>(clinicServices.flat().filter(Boolean as any)));

        // 3) Fetch general and private requests for this profession
        const [genResp, privResp] = await Promise.all([
          axios.get(`${baseUrl}/collaborations/general-requests`, {
            params: {
              page_size: 50,
              ...(serviceIds.length ? { service_ids: serviceIds.join(',') } : {})
            }
          }),
          axios.get(`${baseUrl}/collaborations/private-requests`, { params: { page_size: 50 } })
        ]);

        const genData: ServiceRequest[] = genResp.data?.data?.requests || [];
        const privData: ServiceRequest[] = (privResp.data?.data?.requests || []).filter((r: any) => clinicList.some(c => c.id === (r.creator?.id || null)));

        // 4) Fetch collaborations by category id
        const collResp = await axios.get(`${baseUrl}/collaborations/by-category/`, { params: { category_id: found.id, page_size: 20 } });
        const collData: Collaboration[] = collResp.data?.data?.collaborations || [];

        if (mounted) {
          setGeneralRequests(genData);
          setPrivateRequests(privData);
          setCollaborations(collData);
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load profession data");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [professionSlug, baseUrl]);

  return (
    <>
      <PartneHeader partner_name={partnerName} partner_id={partnerId} partner_cat={partnerCat} partner_logo={partnerLogo} />
      <PartnerMenu />

      <div className="vertical-overlay"></div>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0">{category?.name || professionSlug}</h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item"><a href="/partner/home">Partner</a></li>
                      <li className="breadcrumb-item active">Dashboard</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {loading && (
              <div className="alert alert-info">Loading profession data...</div>
            )}
            {error && (
              <div className="alert alert-danger">{error}</div>
            )}

            <div className="row mt-4">
              <div className="col">
                <div className="card card-animate">
                  <div className="card-header">
                    <h6 className="mb-0">Overview</h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex gap-3 flex-wrap">
                      <div className="badge bg-primary-subtle text-primary p-3 rounded-3">Clinics: {clinics.length}</div>
                      <div className="badge bg-success-subtle text-success p-3 rounded-3">General Requests: {generalRequests.length}</div>
                      <div className="badge bg-warning-subtle text-warning p-3 rounded-3">Private Requests: {privateRequests.length}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12 col-lg-6">
                <div className="card card-animate h-100">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Recent General Requests</h6>
                    <a className="btn btn-sm btn-outline-primary" href={`/partner/${professionSlug}/services`}>View Services</a>
                  </div>
                  <div className="card-body">
                    {generalRequests.slice(0, 5).map(req => (
                      <div key={req.id} className="mb-3">
                        <div className="fw-semibold">{req.title}</div>
                        <div className="text-muted small">{req.description}</div>
                        <div className="text-muted small">Clinic: {req.creator?.name || 'N/A'} · Status: {req.status} · Priority: {req.priority}</div>
                      </div>
                    ))}
                    {generalRequests.length === 0 && (
                      <div className="text-muted">No requests found for this profession.</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-6 mt-3 mt-lg-0">
                <div className="card card-animate h-100">
                  <div className="card-header">
                    <h6 className="mb-0">Recent Private Requests</h6>
                  </div>
                  <div className="card-body">
                    {privateRequests.slice(0, 5).map(req => (
                      <div key={req.id} className="mb-3">
                        <div className="fw-semibold">{req.title}</div>
                        <div className="text-muted small">{req.description}</div>
                        <div className="text-muted small">Clinic: {req.creator?.name || 'N/A'} · Status: {req.status} · Priority: {req.priority}</div>
                      </div>
                    ))}
                    {privateRequests.length === 0 && (
                      <div className="text-muted">No private requests for this profession.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col">
                <div className="card card-animate">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Recent Collaborations</h6>
                    <span className="badge bg-info-subtle text-info">{collaborations.length}</span>
                  </div>
                  <div className="card-body">
                    {collaborations.slice(0, 6).map(col => (
                      <div key={col.id} className="mb-3">
                        <div className="fw-semibold">{col.title}</div>
                        <div className="text-muted small">Partner: {col.creating_partner?.name || 'N/A'} · EHR Access: {col.ehr_access_granted ? 'Yes' : 'No'}</div>
                      </div>
                    ))}
                    {collaborations.length === 0 && (
                      <div className="text-muted">No collaborations found for this profession.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        <DashboardFooter />
      </div>

      <Canvas />
    </>
  );
};

export default ProfessionHome;
